import API from "@/axios/API";
import CommonTable from "@/components/Common/Table";
import DateRangePicker from "@/components/Dashboard/Charts/DateRangePicker";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import ErrorOutlineRoundedIcon from "@mui/icons-material/ErrorOutlineRounded";
import PauseRoundedIcon from "@mui/icons-material/PauseRounded";
import PlayArrowRoundedIcon from "@mui/icons-material/PlayArrowRounded";
import TimelineRoundedIcon from "@mui/icons-material/TimelineRounded";
import { Box, Chip, IconButton, Stack, Tooltip, Typography } from "@mui/material";
import { useCallback, useEffect, useMemo, useState } from "react";
import { THEME, statusRole } from "./theme";

const MONITORING_BASE = `${import.meta.env.VITE_BACKEND_API || import.meta.env.BACKEND_API || ""}/auth/admin/monitoring`;
const PAGE_SIZE = 20;
const LIVE_POLL_MS = 15000;

const METHODS = ["GET", "POST", "PUT", "PATCH", "DELETE"];

const STATUS_META = {
    good: { label: "2xx", bg: THEME.successBg, fg: THEME.success },
    neutral: { label: "3xx", bg: THEME.bgTableHeader, fg: THEME.textSecondary },
    warning: { label: "4xx", bg: THEME.warningBg, fg: "#B45309" },
    critical: { label: "5xx", bg: THEME.errorBg, fg: "#C62828" },
};

const fmtBytes = (bytes) => {
    if (bytes == null) return "—";
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
};

const fmtDuration = (ms) => (ms == null ? "—" : ms >= 1000 ? `${(ms / 1000).toFixed(2)}s` : `${ms}ms`);

const fmtTime = (v) => {
    const d = new Date(v);
    if (Number.isNaN(d.getTime())) return "—";
    return d.toLocaleString("en-US", { month: "short", day: "2-digit", hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false });
};

const StatusChip = ({ httpStatus }) => {
    const meta = STATUS_META[statusRole(httpStatus)];
    return (
        <Chip
            size="small"
            label={`${httpStatus} · ${meta.label}`}
            sx={{
                height: 22,
                fontSize: 11,
                fontWeight: 700,
                fontFamily: THEME.mono,
                bgcolor: meta.bg,
                color: meta.fg,
            }}
        />
    );
};

const buildColumns = () => [
    {
        key: "startTime",
        label: "Time",
        filterable: true,
        filterConfig: {
            heading: "Sort by Time",
            sortOptions: [
                { dir: "DESC", label: "Newest first" },
                { dir: "ASC", label: "Oldest first" },
            ],
        },
        cellSx: { fontSize: 12, color: THEME.textSecondary, whiteSpace: "nowrap" },
        render: (r) => fmtTime(r.startTime),
    },
    {
        key: "method",
        label: "Method",
        filterable: true,
        filterConfig: {
            heading: "Filter by Method",
            chipsRadio: true,
            chips: METHODS.map((m) => ({ value: m, label: m })),
        },
        render: (r) => (
            <Typography sx={{ fontSize: 11.5, fontWeight: 700, fontFamily: THEME.mono, color: THEME.primary }}>{r.method}</Typography>
        ),
    },
    {
        key: "path",
        label: "Path",
        filterable: true,
        filterConfig: { heading: "Filter by Path", text: true, textPlaceholder: "e.g. /orders/{id}" },
        cellSx: { fontSize: 12, fontFamily: THEME.mono, color: THEME.textPrimary, wordBreak: "break-all" },
        render: (r) => r.path,
    },
    {
        key: "httpStatus",
        label: "Status",
        filterable: true,
        filterConfig: { heading: "Filter by Status Code", text: true, textPlaceholder: "e.g. 200, 404" },
        render: (r) => <StatusChip httpStatus={r.httpStatus} />,
    },
    {
        key: "durationMs",
        label: "Duration",
        cellSx: { fontSize: 12, fontFamily: THEME.mono, color: THEME.textPrimary, whiteSpace: "nowrap" },
        render: (r) => fmtDuration(r.durationMs),
    },
    {
        key: "cpuTimeMs",
        label: "CPU",
        cellSx: { fontSize: 12, fontFamily: THEME.mono, color: THEME.textSecondary, whiteSpace: "nowrap" },
        render: (r) => (r.cpuTimeMs != null ? `${r.cpuTimeMs.toFixed(1)}ms` : "—"),
    },
    {
        key: "memAllocatedBytes",
        label: "Memory",
        cellSx: { fontSize: 12, fontFamily: THEME.mono, color: THEME.textSecondary, whiteSpace: "nowrap" },
        render: (r) => fmtBytes(r.memAllocatedBytes),
    },
    {
        key: "mongoCallCount",
        label: "Mongo Calls",
        cellSx: { fontSize: 12, fontFamily: THEME.mono, color: THEME.textSecondary, textAlign: "center" },
        render: (r) => r.mongoCallCount ?? 0,
    },
    {
        key: "userEmail",
        label: "User",
        cellSx: { fontSize: 12, color: THEME.textPrimary, maxWidth: 180, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
        render: (r) => r.userEmail || "—",
    },
    {
        key: "traceId",
        label: "Trace ID",
        cellSx: { fontSize: 11, fontFamily: THEME.mono, color: THEME.textSecondary },
        render: (r) => (r.traceId || "").slice(0, 8),
    },
];

export default function LiveRequestsTab() {
    const [requests, setRequests] = useState([]);
    const [totalElements, setTotal] = useState(0);
    const [page, setPage] = useState(0);
    const [filters, setFilters] = useState({});
    const [sort, setSort] = useState({ key: "startTime", dir: "DESC" });
    const [dateRange, setDateRange] = useState(null); // [fromISO, toISO] or null (backend defaults to last 24h)
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [autoRefresh, setAutoRefresh] = useState(true);

    const fetchLogs = useCallback(async (pageNum, filtersVal, sortVal, rangeVal, { silent } = {}) => {
        if (!silent) setLoading(true);
        setError(null);
        try {
            const params = new URLSearchParams();
            params.set("page", pageNum);
            params.set("size", PAGE_SIZE);

            const pathFilter = filtersVal?.path?.text;
            if (pathFilter) params.set("path", pathFilter);

            const methodFilter = filtersVal?.method?.chips?.[0];
            if (methodFilter) params.set("method", methodFilter);

            const statusFilter = filtersVal?.httpStatus?.text;
            if (statusFilter) params.set("httpStatus", statusFilter);

            if (rangeVal) {
                params.set("from", rangeVal[0]);
                params.set("to", rangeVal[1]);
            }

            const res = await API.get(`${MONITORING_BASE}/requests/history?${params.toString()}`);
            const data = res.data;
            setRequests(data.items ?? []);
            setTotal(data.totalElements ?? 0);
        } catch (err) {
            setError(err.message || "Failed to load requests.");
        } finally {
            if (!silent) setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchLogs(page, filters, sort, dateRange);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, filters, sort, dateRange]);

    /* live auto-refresh: only when viewing page 0 with no explicit date range */
    useEffect(() => {
        if (!autoRefresh || dateRange || page !== 0) return undefined;
        const interval = setInterval(() => fetchLogs(0, filters, sort, null, { silent: true }), LIVE_POLL_MS);
        return () => clearInterval(interval);
    }, [autoRefresh, dateRange, page, filters, sort, fetchLogs]);

    const handleApplyFilter = useCallback((colKey, val) => {
        setFilters((prev) => ({ ...prev, [colKey]: val }));
        setPage(0);
    }, []);

    const handleClearFilter = useCallback((colKey) => {
        setFilters((prev) => {
            const next = { ...prev };
            delete next[colKey];
            return next;
        });
        setPage(0);
    }, []);

    const handleSort = useCallback((colKey, dir) => {
        setSort({ key: colKey, dir });
        setPage(0);
    }, []);

    const handleClearAll = () => {
        setFilters({});
        setSort({ key: "startTime", dir: "DESC" });
        setDateRange(null);
        setPage(0);
    };

    const handleDateChange = (dayjsRange) => {
        if (!dayjsRange?.[0] || !dayjsRange?.[1]) return;
        setDateRange([dayjsRange[0].startOf("day").toISOString(), dayjsRange[1].endOf("day").toISOString()]);
        setPage(0);
    };

    const activeFilterCount = useMemo(() => {
        let count = 0;
        Object.values(filters).forEach((v) => {
            if (!v) return;
            if (v.text) count++;
            count += (v.chips || []).length;
        });
        if (dateRange) count++;
        return count;
    }, [filters, dateRange]);

    const columns = useMemo(() => buildColumns(), []);

    const toolbarRight = (
        <Stack direction="row" alignItems="center" spacing={1.5}>
            <Tooltip title={autoRefresh ? "Pause live updates" : "Resume live updates"} arrow>
                <IconButton
                    size="small"
                    onClick={() => setAutoRefresh((v) => !v)}
                    sx={{ border: `1px solid ${THEME.border}`, color: THEME.textPrimary }}
                >
                    {autoRefresh ? <PauseRoundedIcon sx={{ fontSize: 16 }} /> : <PlayArrowRoundedIcon sx={{ fontSize: 16 }} />}
                </IconButton>
            </Tooltip>
            <DateRangePicker size="small" bubbleUpDateOnChange={handleDateChange} />
            <Box
                component="button"
                onClick={handleClearAll}
                sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.75,
                    border: `1px solid ${THEME.primaryBorder}`,
                    bgcolor: THEME.primary08,
                    borderRadius: "8px",
                    px: 1.5,
                    py: 0.6,
                    fontSize: 12.5,
                    fontWeight: 600,
                    color: THEME.primary,
                    cursor: "pointer",
                }}
            >
                <CloseRoundedIcon sx={{ fontSize: 14 }} />
                Clear all
                <Box
                    component="span"
                    sx={{
                        ml: 0.5,
                        px: 0.6,
                        minWidth: 18,
                        height: 18,
                        borderRadius: "999px",
                        bgcolor: THEME.primary,
                        color: "#fff",
                        fontSize: 10,
                        fontWeight: 700,
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                    }}
                >
                    {activeFilterCount}
                </Box>
            </Box>
        </Stack>
    );

    const emptyState = (
        <Box sx={{ py: 4, display: "flex", flexDirection: "column", alignItems: "center", gap: 1 }}>
            <TimelineRoundedIcon sx={{ fontSize: 36, color: THEME.textSecondary, opacity: 0.4 }} />
            <Typography sx={{ fontSize: 13, color: THEME.textSecondary }}>No requests found for this range.</Typography>
        </Box>
    );

    return (
        <Box>
            {error && (
                <Box
                    sx={{
                        mb: 2,
                        px: 2,
                        py: 1.25,
                        borderRadius: `${THEME.radius.card}px`,
                        bgcolor: THEME.errorBg,
                        border: "1px solid #FFCDD2",
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                    }}
                >
                    <ErrorOutlineRoundedIcon sx={{ fontSize: 18, color: "#C62828", flexShrink: 0 }} />
                    <Typography sx={{ fontSize: 13, color: "#B71C1C", flex: 1 }}>{error}</Typography>
                </Box>
            )}

            <CommonTable
                columns={columns}
                data={requests}
                rowKey="id"
                loading={loading}
                skeletonRows={PAGE_SIZE}
                toolbarRight={toolbarRight}
                emptyState={emptyState}
                totalCount={totalElements}
                page={page}
                rowsPerPage={PAGE_SIZE}
                onPageChange={(_, newPage) => setPage(newPage)}
                onApplyFilter={handleApplyFilter}
                onClearFilter={handleClearFilter}
                onSort={handleSort}
                filters={filters}
            />
        </Box>
    );
}
